import { useRef } from "react";
import { FiPaperclip, FiX } from "react-icons/fi";
import { toast } from "react-toastify";

// Pure presentational component. Holds no state — parent owns the File ref.
//
// Props:
//   file:          File | null               — currently attached file
//   onChange:      (File | null) => void     — called when user picks/clears
//   autoAttached:  boolean                   — show "Auto-attached from import"
//   disabled:      boolean                   — disable picker (e.g. while submitting)
export default function AttachPurchaseAgreement({
  file,
  onChange,
  autoAttached = false,
  disabled = false,
}) {
  const inputRef = useRef(null);

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      // Reset the input so picking the same file again re-fires onChange.
      e.target.value = "";
      onChange(null);
      // eslint-disable-next-line no-alert
      toast.error("Please attach a PDF file.");
      return;
    }
    onChange(f);
    e.target.value = "";
  };

  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="text-sm font-medium text-gray-700 mb-1">
        Attach Purchase Agreement (PDF)
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={handlePick}
        disabled={disabled}
      />
      {file ? (
        <div className="flex items-center justify-between gap-2 mt-1">
          <div className="flex items-center gap-2 min-w-0">
            <FiPaperclip className="shrink-0 text-gray-500" />
            <div className="min-w-0">
              <div className="truncate text-sm text-gray-900">{file.name}</div>
              {autoAttached && (
                <div className="text-xs text-gray-500">
                  Auto-attached from import
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-gray-500 hover:text-gray-800 p-1"
            disabled={disabled}
            aria-label="Remove attached purchase agreement"
          >
            <FiX />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="mt-1 px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          Choose PDF…
        </button>
      )}
    </div>
  );
}
